/* eslint-disable no-inline-comments */
import Vue from 'vue'
import Router from 'vue-router'
import store from '@/store'
import get from 'lodash/get'

//- App pages (ie. stuff that gets shown in router view)
import Start from '@/pages/Start'
import Notifications from '@/pages/Notifications'
import LoginReturn from '@/pages/LoginReturn'

//- Status pages
import ForbiddenStatus from '@/pages/status/Forbidden.vue'
import NotFoundStatus from '@/pages/status/NotFound.vue'

Vue.use(Router)

//- Route guard method
const authFor = async (obj, act) => {

  // eslint-disable-next-line no-return-await
  return await (async () => {
    // Wait for app to be initialized
    let isInitialized = await store.getters['app/isInitialized']
    while (!isInitialized) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise(resolve => setTimeout(resolve, 1000));
      // eslint-disable-next-line no-await-in-loop
      isInitialized = await store.getters['app/isInitialized']
    }

    const me = await store.getters['currentuser/currentUserObj']

    //- Not logged in
    if (!(me.data && me.data.id)) {
      return '/'
    }

    //- Else check permissions and return next route
    // eslint-disable-next-line no-return-await
    const nextRoute = await me.can(obj, act).then(res => {
      // eslint-disable-next-line no-undefined
      return res ? undefined : '/forbidden'
    })

    return nextRoute
  })();

}

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/',
      name: 'Start',
      component: Start,
      props: true,
      meta: {
        requiresAuth: false,
        label: 'Hem'
      }
    },
    {
      path: '/loginreturn',
      name: 'LoginReturn',
      component: LoginReturn,
      props: true,
      meta: {
        requiresAuth: false
      }
    },
    {
      path: '/statistics',
      name: 'Statistics',
      component: () =>
        import(
          /* webpackChunkName: "administration" */ '@/pages/Statistics'
        ),
      beforeEnter: async (to, from, next) => {
        await authFor('application', 'list').then(res => {
          return next(res)
        })
      },
      props: true,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/admin/SEQFLevels',
      name: 'SEQFLevels',
      component: () =>
        import(
          /* webpackChunkName: "administration" */ '@/pages/SEQFadministration'
        ),
      beforeEnter: async (to, from, next) => {
        await authFor('admin', 'list').then(res => {
          return next(res)
        })
      },
      props: true,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/admin/book-elements',
      name: 'BookElements',
      component: () =>
        import(
          /* webpackChunkName: "administration" */ '@/pages/admin/BookElements'
        ),
      beforeEnter: async (to, from, next) => {
        await authFor('admin', 'list').then(res => {
          return next(res)
        })
      },
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/admin/professioncourses',
      name: 'ProfessionCourses',
      component: () =>
        import(
          /* webpackChunkName: "administration" */ '@/pages/ProfessionCourses'
        ),
      beforeEnter: async (to, from, next) => {
        await authFor('admin', 'list').then(res => {
          return next(res)
        })
      },
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/admin/professionwidening',
      name: 'ProfessionWidening',
      component: () =>
        import(
          /* webpackChunkName: "administration" */ '@/pages/admin/ProfessionWidening'
        ),
      beforeEnter: async (to, from, next) => {
        await authFor('admin', 'list').then(res => {
          return next(res)
        })
      },
      meta: {
        requiresAuth: true
      }
    },

    {
      path: '/orders',
      name: 'Orders',
      component: () =>
        import(
          /* webpackChunkName: "administration" */ '@/pages/order/OrderList'
        ),

      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/order/:id/distanceorder',
      name: 'OrderDetail',
      component: () =>
        import(/* webpackChunkName: "people" */ '@/pages/order/OrderDetail'),

      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/order/:id/extensionorder',
      name: 'ExtensionOrder',
      component: () =>
        import(/* webpackChunkName: "people" */ '@/pages/order/ExtensionOrder'),
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/order/:id/printorder',
      name: 'PrintOrderDetail',
      component: () =>
        import(/* webpackChunkName: "people" */ '@/pages/order/PrintOrderDetail'),

      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/admin/:item',
      name: 'AdminTypes',
      component: () =>
        import(/* webpackChunkName: "administration" */ '@/pages/AdminTypes'),
      beforeEnter: async (to, from, next) => {
        await authFor('admin', 'list').then(res => {
          return next(res)
        })
      },
      props: true,
      meta: {
        requiresAuth: true
      }
    },

    //- COMPANIES
    {
      path: '/companies',
      name: 'Companies',
      component: () =>
        import(/* webpackChunkName: "companies" */ '@/pages/company/Companies'),
      beforeEnter: async (to, from, next) => {
        await authFor('company', 'list').then(res => {
          return next(res)
        })
      },
      meta: {
        requiresAuth: true,
        label: 'Lista företag'
      }
    },
    {
      path: '/companies/new',
      name: 'CompanyNew',
      component: () =>
        import(/* webpackChunkName: "companies" */ '@/pages/company/Company'),
      beforeEnter: async (to, from, next) => {
        await authFor('company', 'create').then(res => {
          return next(res)
        })
      },
      meta: {
        requiresAuth: true,
        label: 'Nytt företag'
      }
    },
    {
      path: '/companies/:id',
      name: 'Company',
      component: () =>
        import(/* webpackChunkName: "companies" */ '@/pages/company/Company'),
      beforeEnter: async (to, from, next) => {
        await authFor('company', 'read').then(res => {
          return next(res)
        })
      },
      meta: {
        requiresAuth: true
      }
    },

    //- APPLICATIONS

    {
      path: '/applications',
      name: 'Applications',
      component: () =>
        import(/* webpackChunkName: "applications" */ '@/pages/Applications'),
      beforeEnter: async (to, from, next) => {
        await authFor('application', 'list').then(res => {
          return next(res)
        })
      },
      meta: {
        requiresAuth: true,
        label: 'Lista anmälningar'
      }
    },
    {
      path: '/applications/new',
      name: 'ApplicationNew',
      component: () =>
        import(/* webpackChunkName: "applications" */ '@/pages/Application'),
      beforeEnter: async (to, from, next) => {
        await authFor('application', 'create').then(res => {
          return next(res)
        })
      },
      meta: {
        requiresAuth: true,
        label: 'Nytt Anmälningar'
      }
    },
    {
      path: '/applications/:id',
      name: 'Application',
      component: () =>
        import(/* webpackChunkName: "applications" */ '@/pages/Application'),
      beforeEnter: async (to, from, next) => {
        await authFor('application', 'read').then(res => {
          return next(res)
        })
      },
      meta: {
        requiresAuth: true
      }
    },

    //- USERS

    {
      path: '/users',
      name: 'Users',
      component: () => import(/* webpackChunkName: "users" */ '@/pages/Users'),
      beforeEnter: async (to, from, next) => {
        console.log('be')
        await authFor('user', 'list').then(res => {
          return next(res)
        })
        console.log('after')
      },
      meta: {
        requiresAuth: true,
        label: 'Lista användare'
      }
    },
    {
      path: '/users/new',
      name: 'NewUser',
      component: () => import(/* webpackChunkName: "users" */ '@/pages/User'),
      beforeEnter: async (to, from, next) => {
        await authFor('user', 'create').then(res => {
          return next(res)
        })
      },
      meta: {
        requiresAuth: true,
        label: 'Ny användare'
      }
    },
    {
      path: '/users/:id',
      name: 'User',
      component: () => import(/* webpackChunkName: "users" */ '@/pages/User'),
      beforeEnter: async (to, from, next) => {
        const userId = get(to, 'params.id', null)

        const me = await store.getters['currentuser/currentUserObj']
        const currentUserId = get(me, 'data.id', null)

        await (Boolean(currentUserId) && currentUserId === userId ? Promise.resolve(true) : authFor('user', 'read')).then(res => {
          return next(res)
        })
      },
      meta: {
        requiresAuth: true
      }
    },

    //- PEOPLE

    {
      path: '/people/new',
      name: 'PersonNewDetail',
      component: () =>
        import(/* webpackChunkName: "people" */ '@/pages/person/PersonDetail'),
      beforeEnter: async (to, from, next) => {
        await authFor('apprentice', 'read').then(res => {
          return next(res)
        })
      },
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/people/:id',
      name: 'PersonDetail',
      component: () =>
        import(/* webpackChunkName: "people" */ '@/pages/person/PersonDetail'),
      beforeEnter: async (to, from, next) => {
        await authFor('apprentice', 'read').then(res => {
          return next(res)
        })
      },
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/people',
      name: 'PersonList',
      component: () =>
        import(/* webpackChunkName: "people" */ '@/pages/person/PersonList'),
      beforeEnter: async (to, from, next) => {
        await authFor('apprentice', 'list').then(res => {
          return next(res)
        })
      },
      meta: {
        requiresAuth: true,
        label: 'Lista personer'
      }
    },

    {
      path: '/distanceeducations',
      name: 'DistanceEducations',
      component: () =>
        import(/* webpackChunkName: "admin" */ '@/pages/DistanceEducations'),
      beforeEnter: async (to, from, next) => {
        await authFor('admin', 'list').then(res => {
          return next(res)
        })
      },
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/system-settings',
      name: 'SystemSettings',
      component: () =>
        import(/* webpackChunkName: "admin" */ '@/pages/SystemSettings'),
      beforeEnter: async (to, from, next) => {
        await authFor('admin', 'list').then(res => {
          return next(res)
        })
      },
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/instructions',
      name: 'InstructorInstructions',
      component: () =>
        import(/* webpackChunkName: "admin" */ '@/pages/InstructorInstructions'),
      beforeEnter: async (to, from, next) => {
        await authFor('admin', 'list').then(res => {
          return next(res)
        })
      },
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/release-notes',
      name: 'ReleaseNotes',
      component: () =>
        import(/* webpackChunkName: "admin" */ '@/pages/ReleaseNotes'),
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/release-notes/new',
      name: 'ReleaseNoteNew',
      component: () =>
        import(/* webpackChunkName: "admin" */ '@/pages/ReleaseNoteForm'),
      beforeEnter: async (to, from, next) => {
        await authFor('release-note', 'create').then(res => {
          return next(res)
        })
      },
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/release-notes/:id/edit',
      name: 'ReleaseNoteEdit',
      component: () =>
        import(/* webpackChunkName: "admin" */ '@/pages/ReleaseNoteEdit'),
      beforeEnter: async (to, from, next) => {
        await authFor('release-note', 'create').then(res => {
          return next(res)
        })
      },
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/failed-educations',
      name: 'FailedEducations',
      component: () =>
        import(/* webpackChunkName: "admin" */ '@/pages/admin/FailedEducations'),
      beforeEnter: async (to, from, next) => {
        await authFor('admin', 'list').then(res => {
          return next(res)
        })
      },
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/forbidden',
      name: 'ForbiddenStatus',
      component: ForbiddenStatus
    },
    {
      path: '/notifications',
      name: 'Notifications',
      component: Notifications,
      props: true
    },
    {
      path: '*',
      name: 'NotFoundStatus',
      component: NotFoundStatus
    }
  ]
})

router.beforeEach((to, from, next) => {
  //- Convenience method to check that we have a user
  const loggedIn = localStorage.getItem('user')

  if (to.matched.some(record => record.meta.requiresAuth) && !loggedIn) {
    return next('/forbidden')
  }
  return next()
})

export default router
